import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import api, { errMsg } from "../lib/api";

const Context = createContext(null);
export const AttendanceProvider = ({ children }) => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null), [error, setError] = useState(""), [busy, setBusy] = useState(false);
  const active = useRef(false), day = useRef(""), inFlight = useRef(false), mounted = useRef(true);
  const currentUser = useRef(user?.id); currentUser.current = user?.id;
  const load = useCallback(async () => {
    const uid = user?.id;
    try { const { data } = await api.get("/attendance/status"); if (mounted.current && uid === currentUser.current) { setStatus(data); setError(""); day.current = data.date; } }
    catch (e) { if (mounted.current) setError(errMsg(e)); }
  }, [user?.id]);
  const start = useCallback(async () => {
    const uid = user?.id;
    try { const { data } = await api.post("/attendance/session-start"); if (mounted.current && uid === currentUser.current) { setStatus(data); day.current = data.date; setError(""); } }
    catch (e) { if (mounted.current) setError(errMsg(e)); }
  }, [user?.id]);
  useEffect(() => {
    mounted.current = true; setStatus(null); day.current = ""; start();
    const activity = (e) => { if (!document.hidden && e.isTrusted) active.current = true; };
    const events = ["pointerdown", "pointermove", "keydown", "scroll", "touchstart"];
    events.forEach((name) => window.addEventListener(name, activity, { passive: true }));
    const heartbeat = async () => {
      if (inFlight.current) return;
      const parts = new Intl.DateTimeFormat("en-CA", { timeZone: "Australia/Sydney", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date());
      const get = (key) => parts.find((p) => p.type === key).value;
      const today = `${get("year")}-${get("month")}-${get("day")}`;
      inFlight.current = true;
      const uid = user?.id;
      try {
        if (day.current && today !== day.current && !document.hidden) await start();
        const wasActive = active.current && !document.hidden; active.current = false;
        const { data } = await api.post("/attendance/heartbeat", { active: wasActive });
        if (mounted.current && uid === currentUser.current) { setStatus(data); setError(""); }
      } catch (e) { if (mounted.current) setError(errMsg(e)); }
      finally { inFlight.current = false; }
    };
    const visible = () => { if (!document.hidden) { active.current = true; start(); } };
    document.addEventListener("visibilitychange", visible);
    window.addEventListener("online", start);
    // Presence reports are not the cutoff scheduler; server cron handles idle/closed browsers.
    const timer = setInterval(heartbeat, 30000);
    return () => { mounted.current = false; clearInterval(timer); events.forEach((name) => window.removeEventListener(name, activity)); document.removeEventListener("visibilitychange", visible); window.removeEventListener("online", start); };
  }, [user?.id, start]);
  const act = async (action) => {
    if (busy) return;
    setBusy(true);
    try { const { data } = await api.post(`/attendance/${action}`); setStatus(data); setError(""); }
    catch (e) { setError(errMsg(e)); throw e; }
    finally { setBusy(false); }
  };
  return <Context.Provider value={{ status, error, busy, act, refresh: load, retry: start }}>{children}</Context.Provider>;
};
export const useAttendance = () => useContext(Context);